import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UsersIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { LineChart, BarChart, DonutChart, MetricCard } from '../../components/charts/Chart';

const AdminAnalytics = () => {
  const { user, loading: authLoading } = useAuth();
  const [dateRange, setDateRange] = useState('30d'); // 7d, 30d, 90d, 1y, custom
  const [selectedMetrics, setSelectedMetrics] = useState(['revenue', 'orders', 'customers', 'conversion']);
  const [loading, setLoading] = useState(true);
  
  // Analytics data state
  const [analytics, setAnalytics] = useState({
    overview: {},
    revenue: [],
    orders: [],
    customers: [],
    products: [],
    geographic: [],
    funnelData: [],
    cohortData: [],
    predictions: {}
  });

  useEffect(() => {
    if (authLoading) return; // Wait for auth to complete
    
    if (!user) {
      toast.error('אנא התחבר כדי לצפות בדף זה');
      return;
    }
    
    if (user.role !== 'admin') {
      toast.error('אין לך הרשאה לצפות בדף זה - נדרשות הרשאות מנהל');
      return;
    }

    fetchAnalyticsData();
  }, [dateRange, user, authLoading]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Map dateRange to period parameter
      let period = '30d';
      if (dateRange === '7d') period = '7d';
      else if (dateRange === '30d') period = '30d';
      else if (dateRange === '90d') period = '90d';
      else if (dateRange === '1y') period = '1y';

      // Fetch comprehensive analytics from all available endpoints
      const [
        enhancedStatsRes,
        businessIntelligenceRes,
        profitAnalysisRes,
        orderTrendsRes,
        customerTrendsRes,
        topProductsRes,
        geographicRes,
        cashFlowRes
      ] = await Promise.all([
        axios.get('/api/admin/dashboard/enhanced-stats'),
        axios.get('/api/admin/dashboard/business-intelligence'),
        axios.get('/api/admin/dashboard/profit-analysis'),
        axios.get(`/api/admin/analytics/order-trends?period=${period}`),
        axios.get('/api/admin/analytics/customer-trends'),
        axios.get('/api/admin/analytics/top-products?limit=10'),
        axios.get('/api/admin/analytics/geographic'),
        axios.get('/api/admin/dashboard/cash-flow-prediction')
      ]);

      const orderTrends = orderTrendsRes.data.trends || [];
      const totalOrders = orderTrends.reduce((sum, t) => sum + (t.orders || 0), 0);
      const totalRevenue = orderTrends.reduce((sum, t) => sum + (t.revenue || 0), 0);

      // Customer segments for donut chart
      const customerSegments = [
        { label: 'B2C', value: customerTrendsRes.data.segments?.b2c || 0, color: '#3B82F6' },
        { label: 'B2B', value: customerTrendsRes.data.segments?.b2b || 0, color: '#8B5CF6' }
      ];

      // Traffic sources (mock for now - would need real analytics integration)
      const trafficSources = [
        { label: 'ישיר', value: 45, color: '#10B981' },
        { label: 'חיפוש אורגני', value: 30, color: '#3B82F6' },
        { label: 'מדיה חברתית', value: 15, color: '#F59E0B' },
        { label: 'פרסום', value: 10, color: '#EF4444' }
      ];

      // Conversion funnel
      const funnelData = [
        { stage: 'ביקורים באתר', value: totalOrders * 10, percentage: 100 },
        { stage: 'צפייה במוצרים', value: totalOrders * 5, percentage: 50 },
        { stage: 'הוספה לעגלה', value: totalOrders * 2, percentage: 20 },
        { stage: 'תחילת תשלום', value: Math.floor(totalOrders * 1.5), percentage: 15 },
        { stage: 'השלמת הזמנה', value: totalOrders, percentage: 10 }
      ];

      // Transform backend data to match analytics format
      setAnalytics({
        overview: {
          totalRevenue: totalRevenue,
          totalOrders: totalOrders,
          totalCustomers: enhancedStatsRes.data.totalCustomers || 0,
          conversionRate: funnelData[4].percentage,
          avgOrderValue: totalOrders > 0 ? totalRevenue / totalOrders : 0,
          revenueGrowth: enhancedStatsRes.data.revenueChange || 0,
          ordersGrowth: enhancedStatsRes.data.ordersChange || 0,
          customersGrowth: enhancedStatsRes.data.customersChange || 0,
          conversionGrowth: 0,
          customerSatisfaction: enhancedStatsRes.data.customerSatisfaction || 4.5,
          customerLifetimeValue: totalOrders > 0 ? Math.round(totalRevenue / (enhancedStatsRes.data.totalCustomers || 1)) : 0
        },
        revenue: orderTrendsRes.data.trends?.map(t => ({
          label: t.label,
          value: t.revenue
        })) || [],
        orders: orderTrendsRes.data.trends?.map(t => ({
          label: t.label,
          value: t.orders
        })) || [],
        customers: customerTrendsRes.data.trends?.map((t, i) => ({
          label: new Date(t.date).toLocaleDateString('he-IL', { month: 'short', day: 'numeric' }),
          value: t.new_customers
        })) || [],
        products: topProductsRes.data.products?.map(p => ({
          name: p.name,
          value: p.revenue,
          label: p.name
        })) || [],
        customerSegments,
        trafficSources,
        geographic: geographicRes.data.data || [],
        funnelData,
        cohortData: [], // Would need cohort analysis
        predictions: {
          nextMonthRevenue: cashFlowRes.data.projections?.[0]?.income || 0,
          nextMonthOrders: Math.round(totalOrders * 1.1),
          growthForecast: 10,
          seasonalTrends: cashFlowRes.data.projections?.slice(0, 6).map(p => ({
            month: p.month,
            predicted: p.income
          })) || []
        }
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Show empty state with error indication
      setAnalytics({
        overview: {
          error: true,
          errorMessage: error.response?.data?.message || error.message || 'שגיאה בטעינת הנתונים'
        },
        revenue: [],
        orders: [],
        customers: [],
        products: [],
        customerSegments: [],
        trafficSources: [],
        geographic: [],
        funnelData: [],
        cohortData: [],
        predictions: {}
      });
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">אין הרשאה</h2>
          <p className="text-gray-600">אין לך הרשאה לצפות בדף זה</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">אנליטיקה ודוחות</h1>
          <p className="text-gray-600 mt-2">ניתוח נתונים מתקדם ותחזיות עסקיות</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input text-sm"
          >
            <option value="7d">7 ימים אחרונים</option>
            <option value="30d">30 ימים אחרונים</option>
            <option value="90d">90 ימים אחרונים</option>
            <option value="1y">שנה אחרונה</option>
          </select>
          
          <button className="btn btn-outline flex items-center">
            <DocumentArrowDownIcon className="h-4 w-4 ml-2" />
            יצוא נתונים
          </button>
          
          <button className="btn btn-outline flex items-center">
            <FunnelIcon className="h-4 w-4 ml-2" />
            מסננים
          </button>
        </div>
      </div>

      {/* Real-time Metrics */}
      <div className="bg-gradient-to-l from-primary-600 to-primary-700 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">מדדים בזמן אמת</h3>
          <div className="flex items-center text-green-300">
            <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
            <span className="text-sm">מעודכן כעת</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold">47</div>
            <div className="text-sm text-primary-200">משתמשים פעילים</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">12</div>
            <div className="text-sm text-primary-200">הזמנות פעילות</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">68%</div>
            <div className="text-sm text-primary-200">עומס מערכת</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">245ms</div>
            <div className="text-sm text-primary-200">זמן תגובה</div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {analytics.overview?.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="mr-3">
              <h3 className="text-sm font-medium text-red-800">שגיאה בטעינת הנתונים</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{analytics.overview.errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="סך הכנסות"
          value={formatCurrency(analytics.overview?.totalRevenue || 0)}
          change={analytics.overview?.revenueGrowth || 0}
          trend={(analytics.revenue || []).slice(-7).map(d => d.value)}
          icon={CurrencyDollarIcon}
          color="green"
        />
        
        <MetricCard
          title="הזמנות"
          value={(analytics.overview?.totalOrders || 0).toLocaleString('he-IL')}
          change={analytics.overview?.ordersGrowth || 0}
          trend={analytics.orders?.slice(-7)?.map(d => d.value) || []}
          icon={ShoppingCartIcon}
          color="blue"
        />
        
        <MetricCard
          title="לקוחות"
          value={(analytics.overview?.totalCustomers || 0).toLocaleString('he-IL')}
          change={analytics.overview?.customersGrowth || 0}
          icon={UsersIcon}
          color="purple"
        />
        
        <MetricCard
          title="שיעור המרה"
          value={`${analytics.overview?.conversionRate || 0}%`}
          change={analytics.overview?.conversionGrowth || 0}
          icon={ArrowTrendingUpIcon}
          color={(analytics.overview?.conversionGrowth || 0) >= 0 ? 'green' : 'red'}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <LineChart
          data={analytics.revenue || []}
          title="מגמות הכנסות"
          color="#10B981"
          height={300}
        />
        
        {/* Orders Trend */}
        <LineChart
          data={analytics.orders || []}
          title="מגמות הזמנות"
          color="#3B82F6"
          height={300}
        />
      </div>

      {/* Additional Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Products */}
        <BarChart
          data={analytics.products || []}
          title="מוצרים מובילים"
          color="#8B5CF6"
        />
        
        {/* Customer Segments */}
        <DonutChart
          data={analytics.customerSegments || []}
          title="פילוח לקוחות"
          centerText={{
            value: '100%',
            label: 'לקוחות'
          }}
        />
        
        {/* Traffic Sources */}
        <DonutChart
          data={analytics.trafficSources || []}
          title="מקורות תנועה"
          centerText={{
            value: '100%',
            label: 'ביקורים'
          }}
        />
      </div>

      {/* Advanced Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">משפך המרות</h3>
          <div className="space-y-3">
            {(analytics.funnelData || []).map((stage, index) => (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                  <div className="flex items-center space-x-4 space-x-reverse">
                    <span className="text-sm text-gray-500">{stage.percentage}%</span>
                    <span className="text-sm font-medium text-gray-900">
                      {(stage.value || 0).toLocaleString('he-IL')}
                    </span>
                  </div>
                </div>
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-6">
                    <div
                      className="bg-gradient-to-l from-primary-400 to-primary-600 h-6 rounded-full transition-all duration-1000 ease-out flex items-center justify-center"
                      style={{ width: `${stage.percentage}%` }}
                    >
                      {stage.percentage > 15 && (
                        <span className="text-white text-xs font-medium">
                          {stage.percentage}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Drop-off indicator */}
                {index < (analytics.funnelData || []).length - 1 && (
                  <div className="text-xs text-red-600 mt-1">
                    נשירה: {((analytics.funnelData || [])[index]?.percentage - (analytics.funnelData || [])[index + 1]?.percentage || 0).toFixed(1)}%
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Geographic Performance */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">ביצועים גאוגרפיים</h3>
          <div className="space-y-4">
            {(analytics.geographic || []).map((region, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{region.region}</div>
                  <div className="text-sm text-gray-500">
                    {(region.orders || 0).toLocaleString('he-IL')} הזמנות
                  </div>
                </div>
                <div className="text-left">
                  <div className="font-medium text-green-600">
                    {formatCurrency(region.revenue || 0)}
                  </div>
                  <div className="text-sm text-gray-500">
                    ממוצע: {formatCurrency((region.revenue || 0) / (region.orders || 1))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Predictions & Forecasting */}
      <div className="bg-gradient-to-l from-indigo-50 to-purple-50 p-6 rounded-lg border border-indigo-200">
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 text-indigo-600 ml-3" />
          <h3 className="text-lg font-semibold text-gray-900">תחזיות וחיזויים</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-indigo-600">
              {formatCurrency(analytics.predictions?.nextMonthRevenue || 0)}
            </div>
            <div className="text-sm text-gray-600">תחזית הכנסות החודש הבא</div>
            <div className="text-xs text-green-600 mt-1">
              צמיחה צפויה: {formatPercent(analytics.predictions?.growthForecast || 0)}
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">
              {(analytics.predictions?.nextMonthOrders || 0).toLocaleString('he-IL')}
            </div>
            <div className="text-sm text-gray-600">תחזית הזמנות החודש הבא</div>
            <div className="text-xs text-blue-600 mt-1">
              ממוצע יומי: {Math.round((analytics.predictions?.nextMonthOrders || 0) / 30).toLocaleString('he-IL')}
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              {(analytics.overview?.customerLifetimeValue || 0).toLocaleString('he-IL')} ₪
            </div>
            <div className="text-sm text-gray-600">ערך לקוח לכל החיים</div>
            <div className="text-xs text-green-600 mt-1">
              מגמה עולה
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">
              {analytics.overview.customerSatisfaction}/5
            </div>
            <div className="text-sm text-gray-600">שביעות רצון לקוחות</div>
            <div className="flex justify-center mt-1">
              {[...Array(5)].map((_, i) => (
                <StarIcon
                  key={i}
                  className={`h-4 w-4 ${
                    i < Math.floor(analytics.overview?.customerSatisfaction || 0)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-indigo-200">
          <h4 className="text-md font-semibold text-gray-800 mb-3">מגמות עונתיות</h4>
          <div className="flex justify-between items-center">
            {(analytics.predictions?.seasonalTrends || []).map((trend, index) => (
              <div key={index} className="text-center">
                <div className="text-lg font-bold text-indigo-600">
                  {formatCurrency(trend.predicted || 0)}
                </div>
                <div className="text-sm text-gray-600">{trend.month || 'N/A'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
